import { FileText, AlertTriangle, Scale, Ban, Shield, Mail } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function Terms() {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions – PlaySpiro</title>
        <meta name="description" content="Read PlaySpiro terms and conditions governing the use of our casino review platform." />
        <link rel="canonical" href="https://playspiro.com/terms" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
    <div className="min-h-screen py-8" data-testid="terms-page">
      {/* Header */}
      <section className="gradient-hero py-12 mb-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 font-['Montserrat']">
            Terms & <span className="text-primary">Conditions</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Last updated: January 2026
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4">
        <div className="glass rounded-2xl p-6 md:p-8 space-y-8">
          {/* Acceptance */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Acceptance of Terms</h2>
            </div>
            <p className="text-white/70 leading-relaxed">
              By accessing and using PlaySpiro (playspiro.com), you accept and agree to be bound by 
              these Terms and Conditions. If you do not agree to these terms, please do not use our 
              website. We reserve the right to modify these terms at any time, and such modifications 
              will be effective immediately upon posting.
            </p>
          </section>

          {/* Use of Website */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-6 h-6 text-secondary" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Use of Website</h2>
            </div>
            <div className="space-y-4 text-white/70">
              <p>
                PlaySpiro is an informational website that provides casino reviews, bonus comparisons, 
                and gambling-related content. We are not a casino and do not offer real-money gambling 
                services.
              </p>
              <p>
                The information provided on this website is for general informational purposes only. 
                We make no representations or warranties of any kind, express or implied, about the 
                completeness, accuracy, reliability, or suitability of the information.
              </p>
            </div>
          </section>

          {/* Age Restriction */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Ban className="w-6 h-6 text-red-400" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Age Restriction</h2>
            </div>
            <p className="text-white/70 leading-relaxed">
              You must be at least 18 years old (or the legal gambling age in your jurisdiction) to 
              use this website. By using PlaySpiro, you confirm that you meet this age requirement. 
              We do not knowingly collect information from anyone under 18 years of age.
            </p>
          </section>

          {/* Affiliate Disclosure */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-400" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Affiliate Disclosure</h2>
            </div>
            <p className="text-white/70 leading-relaxed">
              PlaySpiro participates in affiliate programs and may earn commissions when you click 
              through our links and register at featured casinos. This does not affect our reviews, 
              which are based on independent research and evaluation. Our editorial content is never 
              influenced by affiliate relationships.
            </p>
          </section>

          {/* User Content */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-['Montserrat']">User Content</h2>
            <div className="space-y-4 text-white/70">
              <p>
                When you submit reviews, complaints, or other content to our website, you grant 
                PlaySpiro a non-exclusive, royalty-free, perpetual, and worldwide license to use, 
                reproduce, modify, and publish such content.
              </p>
              <p>
                You agree not to submit content that is:
              </p>
              <ul className="space-y-2 ml-4">
                <li>• False, misleading, or defamatory</li>
                <li>• Infringing on intellectual property rights</li>
                <li>• Illegal or promoting illegal activities</li>
                <li>• Containing malware or harmful code</li>
                <li>• Spam or unsolicited advertising</li>
              </ul>
            </div>
          </section>

          {/* Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-['Montserrat']">Disclaimer</h2>
            <div className="space-y-4 text-white/70">
              <p>
                Gambling involves risk. You should never gamble with money you cannot afford to lose. 
                PlaySpiro is not responsible for any losses incurred from gambling activities at 
                casinos featured on our website.
              </p>
              <p>
                The information on this website is provided "as is" without any warranties. We do not 
                guarantee that the website will be error-free, secure, or continuously available.
              </p>
            </div>
          </section>

          {/* Responsible Gambling */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-green-400" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Responsible Gambling</h2>
            </div>
            <p className="text-white/70 leading-relaxed mb-4">
              We encourage responsible gambling. If you or someone you know has a gambling problem, 
              please seek help from the following organizations:
            </p>
            <ul className="space-y-2 text-white/70">
              <li>• <strong className="text-white">GamCare:</strong> www.gamcare.org.uk</li>
              <li>• <strong className="text-white">BeGambleAware:</strong> www.begambleaware.org</li>
              <li>• <strong className="text-white">Gamblers Anonymous:</strong> www.gamblersanonymous.org</li>
              <li>• <strong className="text-white">National Council on Problem Gambling:</strong> www.ncpgambling.org</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-['Montserrat']">Limitation of Liability</h2>
            <p className="text-white/70 leading-relaxed">
              To the fullest extent permitted by law, PlaySpiro shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages, or any loss of profits or 
              revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, 
              or other intangible losses resulting from your use of our website.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-['Montserrat']">Governing Law</h2>
            <p className="text-white/70 leading-relaxed">
              These Terms and Conditions shall be governed by and construed in accordance with the 
              laws of Malta, without regard to its conflict of law provisions. Any disputes arising 
              from these terms shall be subject to the exclusive jurisdiction of the courts of Malta.
            </p>
          </section>

          {/* Contact */}
          <section className="border-t border-white/10 pt-8">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-secondary" />
              <h2 className="text-2xl font-bold text-white font-['Montserrat']">Contact Us</h2>
            </div>
            <p className="text-white/70 leading-relaxed">
              If you have any questions about these Terms and Conditions, please contact us at:
              <br /><br />
              <strong className="text-white">Email:</strong>{" "}
              <a href="mailto:support@playspiro.com" className="text-primary hover:underline">
                support@playspiro.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
      </>
  );
}